/**
 * Best-of series as the API runs them: one bot, a fresh Dota lobby per game,
 * the same players invited again each time. The score is kept by team, not by
 * the side a team happens to sit on in a given game.
 */

export type SeriesFormat = 'bo1' | 'bo2' | 'bo3' | 'bo5';

export type SeriesFormatInfo = {
  value: SeriesFormat;
  label: string;
  maxGames: number;
  /** Wins that end the series early; null when every game is always played. */
  winsToClinch: number | null;
  hint: string;
};

const BO1: SeriesFormatInfo = { value: 'bo1', label: 'Best of 1', maxGames: 1, winsToClinch: 1, hint: 'One game' };
const BO2: SeriesFormatInfo = { value: 'bo2', label: 'Best of 2', maxGames: 2, winsToClinch: null, hint: 'Two games, can end 1-1' };
const BO3: SeriesFormatInfo = { value: 'bo3', label: 'Best of 3', maxGames: 3, winsToClinch: 2, hint: 'First to 2 wins' };
const BO5: SeriesFormatInfo = { value: 'bo5', label: 'Best of 5', maxGames: 5, winsToClinch: 3, hint: 'First to 3 wins' };

export const SERIES_FORMATS: readonly SeriesFormatInfo[] = [BO1, BO2, BO3, BO5];

/** The API's own default when the format is omitted. */
export const DEFAULT_SERIES_FORMAT: SeriesFormat = BO2.value;

export function isSeriesFormat(value: unknown): value is SeriesFormat {
  return SERIES_FORMATS.some((format) => format.value === value);
}

export function seriesFormatInfo(format: SeriesFormat): SeriesFormatInfo {
  return SERIES_FORMATS.find((candidate) => candidate.value === format) ?? BO2;
}

/** Mirrors the API's SeriesComplete: max games reached, or a side clinched. */
export function seriesComplete(
  format: SeriesFormat,
  radiantWins: number,
  direWins: number,
  gamesPlayed: number,
): boolean {
  const info = seriesFormatInfo(format);
  if (gamesPlayed >= info.maxGames) return true;
  if (info.winsToClinch === null) return false;
  return radiantWins >= info.winsToClinch || direWins >= info.winsToClinch;
}

/**
 * What a Dota lobby says about its series. Valve only knows best-of-three
 * (1) and best-of-five (2); the API runs a bo2 under Valve's best-of-three
 * value and stops after two games, so a lobby alone cannot tell bo2 from bo3.
 * Pass the format the client remembered for that bot to get it right.
 */
export type SeriesLobbyFields = {
  series_type?: number;
  radiant_series_wins?: number;
  dire_series_wins?: number;
};

export type SeriesLobbyView = {
  formatLabel: string;
  maxGames: number;
  gameNumber: number;
  /** Wins of whoever sits on each side right now, as the Dota lobby shows them. */
  radiantWins: number;
  direWins: number;
  /** True when the format comes from the client's own record, not from Dota's value. */
  exact: boolean;
};

const DOTA_SERIES_FORMATS: Record<number, SeriesFormat> = { 1: 'bo3', 2: 'bo5' };

export function describeSeriesLobby(
  lobby: SeriesLobbyFields,
  knownFormat?: SeriesFormat | null,
): SeriesLobbyView | null {
  const format = knownFormat ?? DOTA_SERIES_FORMATS[lobby.series_type ?? 0];
  if (!format) return null;

  const info = seriesFormatInfo(format);
  const radiantWins = lobby.radiant_series_wins ?? 0;
  const direWins = lobby.dire_series_wins ?? 0;
  return {
    formatLabel: info.label,
    maxGames: info.maxGames,
    gameNumber: Math.min(radiantWins + direWins + 1, info.maxGames),
    radiantWins,
    direWins,
    exact: Boolean(knownFormat),
  };
}
